/**
 * Yumz share-cards v2 — Flow B renderer ("I ate this" / dish verdict).
 *
 * Single layout: food poster.
 *  - Dish photo full-bleed top ~65% of the canvas
 *  - Status-tuned scrim transitions photo → solid bottom panel
 *  - Dish name in display weight, restaurant + verdict line under it
 *  - Optional friend polaroid sticker in top-right (never the hero)
 *
 * Canvas 1080×1920 (9:16 story). Status describes the DISH, not the room.
 */

import type { ShareStatus } from "./shareStatus";
import { SHARE_STATUSES, pickStatusCopy } from "./shareStatus";
import type { PreloadedImages } from "./shareCardHelpers";
import {
  loadImage,
  drawStatusPill,
  drawPolaroidMat,
  drawYumzStamp,
  drawGrain,
  drawWrappedText,
  fillRoundedRect,
  canvasToBlob,
  SYSTEM_DISPLAY,
  SYSTEM_BODY,
} from "./shareCardHelpers";

export interface VerdictCardParams {
  restaurantName: string;
  dishName: string;
  dishImageUrl: string;
  status: ShareStatus;
  locationLabel?: string;
  verdictLine?: string;
  friendPhotoUrl?: string;
  friendCaption?: string;
  preloaded?: PreloadedImages;
  quality?: number;
}

export async function renderVerdict(params: VerdictCardParams): Promise<Blob> {
  const {
    restaurantName,
    dishName,
    dishImageUrl,
    status,
    locationLabel,
    verdictLine,
    friendPhotoUrl,
    friendCaption,
    preloaded,
    quality,
  } = params;

  const def = SHARE_STATUSES[status];
  const W = 1080;
  const H = 1920;

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("renderVerdict: 2D context unavailable");
  }

  const imgCache = new Map<string, Promise<HTMLImageElement>>();

  let photoLoaded = false;
  try {
    const dish = await loadImage(dishImageUrl, imgCache, preloaded);
    const scale = Math.max(W / dish.width, H / dish.height);
    const dw = dish.width * scale;
    const dh = dish.height * scale;
    ctx.save();
    if (status === "mid") {
      ctx.filter = "saturate(0.55) contrast(0.95)";
    }
    ctx.drawImage(dish, (W - dw) / 2, (H - dh) / 2, dw, dh);
    ctx.restore();
    if (status === "mid") {
      ctx.fillStyle = "rgba(20,20,20,0.18)";
      ctx.fillRect(0, 0, W, H);
    }
    photoLoaded = true;
  } catch {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, def.palette.bg1);
    g.addColorStop(1, def.palette.bg2);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  drawScrim(ctx, status, W, H, photoLoaded);

  if (friendPhotoUrl) {
    try {
      const friend = await loadImage(friendPhotoUrl, imgCache, preloaded);
      const matW = 220;
      const matH = Math.round(matW * 1.22);
      const cx = W - matW / 2 - 38;
      const cy = matH / 2 + 130;
      drawPolaroidMat(ctx, friend, cx, cy, matW, matH, 7, friendCaption || "w/ friends");
    } catch {
      // Skip sticker on friend-photo failure.
    }
  }

  drawHeaderTag(
    ctx,
    pickStatusCopy(status, "verdictHeaders") || "verdict",
    48,
    72,
    status,
  );
  if (!friendPhotoUrl) {
    drawStatusPill(ctx, status, W - 64, 96, 32, { align: "right" });
  }

  const panelY = Math.round(H * 0.62);
  const panelPad = 56;

  ctx.fillStyle = restaurantRowColor(status, def);
  ctx.font = `800 28px ${SYSTEM_BODY}`;
  ctx.textAlign = "left";
  const restaurantRow = locationLabel
    ? `${restaurantName}  ·  ${locationLabel}`
    : restaurantName;
  ctx.fillText(letterSpace(restaurantRow.toUpperCase()), panelPad, panelY);

  const nameStart = panelY + 56;
  ctx.textAlign = "left";
  const useGoldText = status === "elite";
  if (useGoldText) {
    const grad = ctx.createLinearGradient(0, nameStart - 80, 0, nameStart + 240);
    grad.addColorStop(0, "#fff4cc");
    grad.addColorStop(0.6, "#d4af37");
    grad.addColorStop(1, "#8a6310");
    ctx.fillStyle = grad;
  } else {
    ctx.fillStyle = dishNameColor(status, def);
  }
  ctx.font = `900 96px ${SYSTEM_DISPLAY}`;
  const nextY = drawWrappedText(
    ctx,
    dishName,
    panelPad,
    nameStart,
    W - panelPad * 2,
    96,
    "left",
  );

  const vline = verdictLine ?? pickStatusCopy(status, "verdictLines");
  if (vline) {
    ctx.fillStyle = verdictLineColor(status, def);
    ctx.font = `500 32px ${SYSTEM_BODY}`;
    drawWrappedText(ctx, vline, panelPad, nextY + 20, W - panelPad * 2, 44, "left");
  }

  if (friendPhotoUrl) {
    drawStatusPill(ctx, status, W - 64, panelY - 32, 32, { align: "right" });
  }

  drawGrain(ctx, W, H, 0.4, status === "honest" ? 0.12 : 0.08);
  drawYumzStamp(ctx, status, W, H);

  const q = quality ?? (status === "mid" ? 0.86 : 0.9);
  return canvasToBlob(canvas, q);
}

// ─── local helpers ──────────────────────────────────────────────────

function drawScrim(
  ctx: CanvasRenderingContext2D,
  status: ShareStatus,
  W: number,
  H: number,
  photoLoaded: boolean,
): void {
  const def = SHARE_STATUSES[status];
  const panel = bottomPanelColor(status, def);

  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, photoLoaded ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0)");
  g.addColorStop(0.28, "rgba(0,0,0,0)");
  g.addColorStop(0.58, alpha(panel, 0.55));
  g.addColorStop(0.92, alpha(panel, 0.96));
  g.addColorStop(1, panel);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  if (status === "vibes") {
    const v = ctx.createRadialGradient(W * 0.3, H * 0.78, 0, W * 0.3, H * 0.78, W * 0.7);
    v.addColorStop(0, "rgba(168,85,247,0.28)");
    v.addColorStop(1, "rgba(168,85,247,0)");
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, W, H);
  } else if (status === "elite") {
    const eg = ctx.createLinearGradient(0, H - 6, 0, H);
    eg.addColorStop(0, "rgba(212,175,55,0)");
    eg.addColorStop(1, "rgba(212,175,55,0.45)");
    ctx.fillStyle = eg;
    ctx.fillRect(0, H - 6, W, 6);
  } else if (status === "fire") {
    const hr = ctx.createRadialGradient(W, H, 0, W, H, W * 0.5);
    hr.addColorStop(0, "rgba(255,50,0,0.35)");
    hr.addColorStop(1, "rgba(255,50,0,0)");
    ctx.fillStyle = hr;
    ctx.fillRect(0, 0, W, H);
  }
}

function drawHeaderTag(
  ctx: CanvasRenderingContext2D,
  label: string,
  x: number,
  y: number,
  status: ShareStatus,
): void {
  const def = SHARE_STATUSES[status];
  ctx.save();
  ctx.font = `800 22px ${SYSTEM_BODY}`;
  const text = label.toUpperCase();
  const textW = ctx.measureText(text).width;
  const padX = 18;
  const padY = 12;
  const w = textW + padX * 2;
  const h = 22 + padY * 2;
  const fill = def.texture === "paper" ? "#1a1207" : "rgba(0,0,0,0.45)";
  const fg = def.texture === "paper" ? "#ffffff" : "rgba(255,255,255,0.92)";
  fillRoundedRect(ctx, x, y - h / 2, w, h, h / 2, fill);
  ctx.fillStyle = fg;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + padX, y);
  ctx.restore();
}

function bottomPanelColor(
  status: ShareStatus,
  def: typeof SHARE_STATUSES[ShareStatus],
): string {
  switch (status) {
    case "fire": return "#1a0500";
    case "vibes": return "#0c0617";
    case "honest": return "#f1ebe1";
    case "mid": return "#08080a";
    case "elite": return "#08070a";
    case "cozy": return "#1a0a02";
    case "lowkey": return "#02080d";
    default: return def.palette.bg1;
  }
}

function dishNameColor(
  status: ShareStatus,
  def: typeof SHARE_STATUSES[ShareStatus],
): string {
  if (status === "honest") return "#1a1207";
  if (status === "mid") return "#d6d3c8";
  if (status === "lowkey") return "#d6e2f0";
  return def.palette.text;
}

function restaurantRowColor(
  status: ShareStatus,
  def: typeof SHARE_STATUSES[ShareStatus],
): string {
  if (status === "fire") return "#ffb380";
  if (status === "honest") return "#4a3520";
  if (status === "mid") return "#888780";
  if (status === "elite") return "#d4af37";
  if (status === "cozy") return "#ffd9a8";
  if (status === "lowkey") return "#6a8aab";
  if (status === "vibes") return "#f0abfc";
  return def.palette.subText;
}

function verdictLineColor(
  status: ShareStatus,
  def: typeof SHARE_STATUSES[ShareStatus],
): string {
  if (status === "honest") return "#4a3520";
  return alpha(def.palette.text, 0.82);
}

function alpha(color: string, a: number): string {
  if (/^#[0-9a-f]{6}$/i.test(color)) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  return color;
}

function letterSpace(text: string): string {
  return text.split("").join(" ");
}
